import { DateOnly } from "@/shared/components/date-picker";
import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	GasEntriesData,
	GasEntry,
	GasEntryDayHeatValue,
	GasEntryDraft,
	GasEntryMeter,
	GasEntryMeterReading,
	GasEntryTarget,
	GasEntryTargetZone,
	GasEntryTrackedShare,
} from "../models/GasEntry";

const moment = getMomentTz();

export const getGasEntries = async (
	params: { startDate: DateOnly; endDate: DateOnly },
	signal?: AbortSignal,
): Promise<GasEntriesData> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[USG].[GetGasEntries]",
		[
			{ name: "StartDate", value: params.startDate },
			{ name: "EndDate", value: params.endDate },
		],
		signal,
	);

	if (resp.ok) {
		const tables = get(resp, "data.tables", {});

		return {
			entries: (get(tables, "0.rows", []) as GasEntry[]).map((row) => ({
				...row,
				EntryDate: moment.utc(row.EntryDate).format("YYYY-MM-DD"),
			})),
			readings: get(tables, "1.rows", []) as GasEntryMeterReading[],
			meters: get(tables, "2.rows", []) as GasEntryMeter[],
			targets: get(tables, "3.rows", []) as GasEntryTarget[],
			zoneShares: get(tables, "4.rows", []) as GasEntryTargetZone[],
			dayHeatValues: (get(tables, "5.rows", []) as GasEntryDayHeatValue[]).map(
				(row) => ({
					...row,
					EntryDate: moment.utc(row.EntryDate).format("YYYY-MM-DD"),
					BillPeriodStart:
						row.BillPeriodStart === null
							? null
							: moment.utc(row.BillPeriodStart).format("YYYY-MM-DD"),
					BillPeriodEnd:
						row.BillPeriodEnd === null
							? null
							: moment.utc(row.BillPeriodEnd).format("YYYY-MM-DD"),
					SourceEntryDate:
						row.SourceEntryDate === null
							? null
							: moment.utc(row.SourceEntryDate).format("YYYY-MM-DD"),
				}),
			),
			trackedShares: (get(tables, "6.rows", []) as GasEntryTrackedShare[]).map(
				(row) => ({
					...row,
					EntryDate: moment.utc(row.EntryDate).format("YYYY-MM-DD"),
				}),
			),
		};
	}

	throw new Error(resp.message || "Error fetching gas entries");
};

export const upsertGasEntry = async (draft: GasEntryDraft) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "GasEntryId", value: draft.GasEntryId },
		{ name: "EntryDate", value: draft.EntryDate },
		{ name: "HeatValueBtuPerCf", value: draft.HeatValueBtuPerCf },
		{ name: "Comments", value: draft.Comments },
		{ name: "MeterReadingsJson", value: JSON.stringify(draft.MeterReadings) },
	];

	const resp = await apiService.callV2("[USG].[UpsertGasEntry]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving the gas entry");
	}

	return get(resp, "data.tables.0.rows.0.GasEntryId", null) as number | null;
};

export const deleteGasEntry = async (gasEntryId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[USG].[DeleteGasEntry]", [
		{ name: "GasEntryId", value: gasEntryId },
	]);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting the gas entry");
	}
};
